import { useMemo } from 'react'

import { useParams } from 'react-router-dom'

import { CampaignStatusEnum } from 'src/Types/campaigns'

import { useGetFavoriteCuratorsQuery } from '../favoriteCuratorsApi'

import { SpotifyCampaignReview, useGetCampaignReviewsQuery } from '.'

export type SpotifyCampaignReviewWithFavorite = Omit<
  SpotifyCampaignReview,
  'curator'
> & {
  curator: SpotifyCampaignReview['curator'] & { isFavorite?: boolean }
}

type SpotifyCampaignDetails = {
  reviews?: Array<SpotifyCampaignReviewWithFavorite>
  status?: CampaignStatusEnum
}

export const useSpotifyCampaignReviews = ({
  status,
}: {
  status?: CampaignStatusEnum
} = {}): SpotifyCampaignDetails => {
  const { campaignID } = useParams<{ campaignID: string }>()
  const campaignIdNumber = Number(campaignID)
  const shouldSkip =
    !campaignIdNumber || status === CampaignStatusEnum.APPROVED || !status

  const { data: spotifyCampaignReviews } = useGetCampaignReviewsQuery(
    campaignIdNumber,
    { skip: shouldSkip },
  )

  const { data: favoriteCurators } = useGetFavoriteCuratorsQuery()

  const spotifyCampaignReviewsWithFavoriteCurators = useMemo(
    () =>
      spotifyCampaignReviews?.map(
        (review): SpotifyCampaignReviewWithFavorite => {
          const isFavorite = Boolean(
            favoriteCurators?.find(
              (curator) => curator.curatorUserId === review.curator.userId,
            ),
          )

          return {
            ...review,
            curator: {
              ...review.curator,
              isFavorite,
            },
          }
        },
      ),
    [favoriteCurators, spotifyCampaignReviews],
  )

  return {
    reviews: spotifyCampaignReviewsWithFavoriteCurators,
  }
}
